import { QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type * as S from "@shipyard/shared";
import { ApiError, http, type Input } from "./api";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
      refetchOnWindowFocus: true,
      staleTime: 5_000,
    },
  },
});

export const keys = {
  status: ["auth", "status"] as const,
  me: ["me"] as const,
  apiKeys: ["me", "api-keys"] as const,
  apps: ["apps"] as const,
  app: (id: string) => ["apps", id] as const,
  members: (id: string) => ["apps", id, "members"] as const,
  builds: (appId: string, platform?: string) => ["apps", appId, "builds", platform ?? "all"] as const,
  build: (id: string) => ["builds", id] as const,
  recentBuilds: (limit: number, q: string) => ["builds", "recent", limit, q] as const,
  tags: ["tags"] as const,
  publications: (appId: string) => ["apps", appId, "publications"] as const,
  devices: ["admin", "devices"] as const,
  publicPage: (token: string) => ["public", token] as const,
  publicInstall: (token: string, buildId: string, device: string | null) => ["public", token, buildId, device ?? "none"] as const,
  users: ["admin", "users"] as const,
  orgs: ["orgs"] as const,
  entitlements: ["entitlements"] as const,
  ssoProviders: ["org", "sso-providers"] as const,
  orgBranding: ["org", "branding"] as const,
  billing: ["org", "billing"] as const,
  orgMembers: ["org", "members"] as const,
  orgInvitations: ["org", "invitations"] as const,
  settings: ["admin", "settings"] as const,
  apple: ["admin", "apple"] as const,
  google: ["admin", "google"] as const,
  llm: ["admin", "llm"] as const,
  git: ["admin", "git"] as const,
  gitRepos: (providerId: string, q: string) => ["admin", "git", providerId, "repos", q] as const,
  branches: (appId: string) => ["apps", appId, "git", "branches"] as const,
  commits: (appId: string, branch: string) => ["apps", appId, "git", "commits", branch] as const,
  tfGroups: (appId: string) => ["apps", appId, "testflight-groups"] as const,
  keystore: (appId: string) => ["apps", appId, "keystore"] as const,
  changelogBase: (buildId: string) => ["builds", buildId, "changelog-base"] as const,
};

const ACTIVE: S.BuildStatus[] = ["uploaded", "pushing", "processing", "promoting", "in_review", "pending_release"];
export const isActive = (status: S.BuildStatus) => ACTIVE.includes(status);

// ------------------------------------------------------------------ auth
export const useAuthStatus = () => useQuery({ queryKey: keys.status, queryFn: () => http.get<S.AuthStatus>("/auth/status") });

export const useMe = () =>
  useQuery({
    queryKey: keys.me,
    queryFn: async () => {
      try {
        return await http.get<S.Me>("/me");
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 60_000,
  });

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => http.post("/auth/logout"),
    onSuccess: () => {
      qc.setQueryData(keys.me, null);
      qc.clear();
    },
  });
}

// ------------------------------------------------------------------ organizations
export const useOrgs = () => useQuery({ queryKey: keys.orgs, queryFn: () => http.get<S.Organization[]>("/orgs"), staleTime: 60_000 });
export const useOrgMembers = (enabled = true) => useQuery({ queryKey: keys.orgMembers, queryFn: () => http.get<S.OrgMember[]>("/org/members"), enabled });

export const useOrgInvitations = () => useQuery({ queryKey: keys.orgInvitations, queryFn: () => http.get<S.Invitation[]>("/org/invitations") });

export const useEntitlements = (enabled = true) =>
  useQuery({ queryKey: keys.entitlements, queryFn: () => http.get<S.Entitlements>("/entitlements"), staleTime: 5 * 60_000, enabled });

export const useBilling = (enabled = true) => useQuery({ queryKey: keys.billing, queryFn: () => http.get<S.Billing>("/org/billing"), enabled });

export const useOrgBranding = () => useQuery({ queryKey: keys.orgBranding, queryFn: () => http.get<S.OrgBranding>("/org/branding") });

export const useSsoProviders = (enabled = true) =>
  useQuery({ queryKey: keys.ssoProviders, queryFn: () => http.get<S.SsoProvider[]>("/org/sso-providers"), enabled });

/** Switches the active organization: every cached query belongs to the previous one. */
export function useSwitchOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => http.post<S.Me>(`/orgs/${id}/activate`),
    onSuccess: async (me) => {
      qc.setQueryData(keys.me, me);
      // Reset (not clear) so that mounted queries refetch for the new organization.
      await qc.resetQueries({ predicate: (q) => q.queryKey[0] !== keys.me[0] && q.queryKey[0] !== keys.status[0] });
    },
  });
}

// ------------------------------------------------------------------ apps & builds
export const useApps = () => useQuery({ queryKey: keys.apps, queryFn: () => http.get<S.App[]>("/apps") });
export const useApp = (id: string) => useQuery({ queryKey: keys.app(id), queryFn: () => http.get<S.App>(`/apps/${id}`), enabled: !!id });
export const useMembers = (id: string) => useQuery({ queryKey: keys.members(id), queryFn: () => http.get<S.AppMember[]>(`/apps/${id}/members`) });

export const useBuilds = (appId: string, platform?: S.Platform) =>
  useQuery({
    queryKey: keys.builds(appId, platform),
    queryFn: () => http.get<S.Build[]>(`/apps/${appId}/builds?limit=100${platform ? `&platform=${platform}` : ""}`),
    refetchInterval: (q) => (q.state.data?.some((b) => isActive(b.status)) ? 5_000 : 30_000),
  });

/** Latest builds across every app, optionally matching a build number / version / commit prefix. */
export const useRecentBuilds = (limit = 10, q = "", enabled = true) =>
  useQuery({
    queryKey: keys.recentBuilds(limit, q),
    queryFn: () => http.get<S.Build[]>(`/builds?limit=${limit}${q ? `&q=${encodeURIComponent(q)}` : ""}`),
    refetchInterval: (query) => (query.state.data?.some((b) => isActive(b.status)) ? 5_000 : 30_000),
    placeholderData: (prev) => prev,
    enabled,
  });

export const useTags = () => useQuery({ queryKey: keys.tags, queryFn: () => http.get<S.Tag[]>("/tags"), staleTime: 60_000 });

export const useBuild = (id: string) =>
  useQuery({
    queryKey: keys.build(id),
    queryFn: () => http.get<S.BuildWithEvents>(`/builds/${id}`),
    refetchInterval: (q) => (q.state.data && isActive(q.state.data.status) ? 4_000 : 30_000),
  });

export function useInvalidateBuild() {
  const qc = useQueryClient();
  return (build: { id: string; appId: string }) => {
    void qc.invalidateQueries({ queryKey: keys.build(build.id) });
    void qc.invalidateQueries({ queryKey: ["apps", build.appId, "builds"] });
  };
}

export function useBuildAction<TBody = unknown>(path: (id: string) => string, method: "post" | "put" | "del" = "post") {
  const invalidate = useInvalidateBuild();
  return useMutation({
    mutationFn: ({ build, body }: { build: { id: string; appId: string }; body?: TBody }) =>
      method === "del" ? http.del(path(build.id)) : method === "put" ? http.put(path(build.id), body) : http.post(path(build.id), body),
    onSuccess: (_data, vars) => invalidate(vars.build),
  });
}

// ------------------------------------------------------------------ admin
export const useUsers = (enabled = true) => useQuery({ queryKey: keys.users, queryFn: () => http.get<S.User[]>("/admin/users"), enabled });
export const useSettings = () => useQuery({ queryKey: keys.settings, queryFn: () => http.get<S.InstanceSettings>("/admin/settings") });
export const useAppleCredentials = (enabled = true) =>
  useQuery({ queryKey: keys.apple, queryFn: () => http.get<S.AppleCredential[]>("/admin/apple-credentials"), enabled });
export const useGoogleCredentials = (enabled = true) =>
  useQuery({ queryKey: keys.google, queryFn: () => http.get<S.GoogleCredential[]>("/admin/google-credentials"), enabled });
export const useLlmProviders = (enabled = true) => useQuery({ queryKey: keys.llm, queryFn: () => http.get<S.LlmProvider[]>("/admin/llm-providers"), enabled });
export const useGitProviders = (enabled = true) => useQuery({ queryKey: keys.git, queryFn: () => http.get<S.GitProvider[]>("/admin/git-providers"), enabled });

// ------------------------------------------------------------------ integrations
export const useGitRepos = (providerId: string, q: string) =>
  useQuery({ queryKey: keys.gitRepos(providerId, q), queryFn: () => http.get<S.GitRepo[]>(`/admin/git-providers/${providerId}/repos?q=${encodeURIComponent(q)}`), enabled: !!providerId, staleTime: 60_000 });
export const useGitBranches = (appId: string, enabled = true) =>
  useQuery({ queryKey: keys.branches(appId), queryFn: () => http.get<{ defaultBranch: string | null; branches: S.GitBranch[] }>(`/apps/${appId}/git/branches`), enabled, staleTime: 60_000, retry: false });
export const useGitCommits = (appId: string, branch: string, enabled = true) =>
  useQuery({ queryKey: keys.commits(appId, branch), queryFn: () => http.get<S.GitCommit[]>(`/apps/${appId}/git/commits?branch=${encodeURIComponent(branch)}`), enabled: enabled && !!branch, staleTime: 30_000, retry: false });
export const useTestflightGroups = (appId: string, enabled = true) =>
  useQuery({ queryKey: keys.tfGroups(appId), queryFn: () => http.get<S.TestflightGroup[]>(`/apps/${appId}/testflight-groups`), enabled, staleTime: 60_000, retry: false });
export const useKeystore = (appId: string, enabled = true) =>
  useQuery({ queryKey: keys.keystore(appId), queryFn: () => http.get<S.AndroidKeystore | null>(`/apps/${appId}/android-keystore`), enabled });
export const useChangelogBase = (buildId: string, enabled = true) =>
  useQuery({ queryKey: keys.changelogBase(buildId), queryFn: () => http.get<{ build: { id: string; version: string; buildNumber: string; commitSha: string } | null }>(`/builds/${buildId}/changelog-base`), enabled });

export const useReleaseCandidates = (appId: string, enabled = true) =>
  useQuery({ queryKey: ["apps", appId, "release-candidates"], queryFn: () => http.get<S.ReleaseCandidates>(`/apps/${appId}/release-candidates`), enabled });

// ------------------------------------------------------------------ public pages
export const usePublications = (appId: string, enabled = true) =>
  useQuery({ queryKey: keys.publications(appId), queryFn: () => http.get<S.Publication[]>(`/apps/${appId}/publications`), enabled });
export const useDevices = () =>
  useQuery({ queryKey: keys.devices, queryFn: () => http.get<S.Device[]>("/admin/devices"), refetchInterval: 30_000 });
export const usePublicPage = (token: string) =>
  useQuery({ queryKey: keys.publicPage(token), queryFn: () => http.get<S.PublicPage>(`/public/${token}`), retry: false });

const INSTALL_POLL: Partial<Record<S.ArtifactStatus, number>> = { preparing: 4_000, pending_approval: 10_000 };
export const usePublicInstall = (token: string, buildId: string, device: string | null, enabled = true) =>
  useQuery({
    queryKey: keys.publicInstall(token, buildId, device),
    queryFn: () => http.get<S.PublicInstall>(`/public/${token}/builds/${buildId}/install${device ? `?device=${device}` : ""}`),
    refetchInterval: (q) => (q.state.data ? (INSTALL_POLL[q.state.data.status] ?? false) : false),
    retry: false,
    enabled,
  });

export const useApiKeys = () => useQuery({ queryKey: keys.apiKeys, queryFn: () => http.get<S.ApiKey[]>("/me/api-keys") });

export type CreateAppInput = Input<typeof import("@shipyard/shared").CreateAppBody>;
